import {
  Button,
  Card,
  Input,
  P,
  Stack,
  Tag,
  Title,
  Toggle,
  Tooltip
} from "@introist/react-foundation/v2";
import styled from "styled-components";
import { useNavigate } from "react-router";
import { TopBarRoute } from "components/templates";
import {
  PerformanceReviewThresholds,
  usePerformanceReviewConfig
} from "../hooks/usePerformanceReviewConfig";
import { PerformanceReviewScenarioType } from "../hooks/usePerformanceReviewData";

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Muted = styled(P)`
  color: var(--palette-text-subdued);
  font-size: 0.875rem;
`;

const SignalCard = styled(Card)<{ $disabled?: boolean }>`
  padding: var(--spacing-large);
  border: 1px solid var(--palette-border-default);
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  transition: opacity 0.15s ease;
`;

const SignalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--spacing-large);
`;

const SignalMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xSmall);
  flex: 1;
`;

const SignalTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-small);
`;

const ThresholdSection = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? "flex" : "none")};
  flex-direction: column;
  gap: var(--spacing-small);
  padding-top: var(--spacing-medium);
  margin-top: var(--spacing-medium);
  border-top: 1px solid var(--palette-border-default);
`;

const ThresholdRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-medium);
`;

const ThresholdLabel = styled(Muted)`
  display: flex;
  align-items: center;
  gap: var(--spacing-xSmall);
`;

const PageDescription = styled(Muted)`
  margin-bottom: var(--spacing-small);
`;

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

type SignalConfig = {
  type: PerformanceReviewScenarioType;
  label: string;
  description: string;
  severity: "high" | "medium" | "varies";
  thresholds?: Array<{
    key: keyof PerformanceReviewThresholds;
    label: string;
    tooltip: string;
    unit: string;
  }>;
};

const signalConfigs: SignalConfig[] = [
  {
    type: "NO_REVIEW_12M",
    label: "Overdue Review",
    description: "Flags employees who have not had a performance review within the configured number of days.",
    severity: "varies",
    thresholds: [
      {
        key: "noReview12mDays",
        label: "Medium severity after",
        tooltip: "Days since last review before this signal is raised at medium severity",
        unit: "days"
      },
      {
        key: "noReview12mHighDays",
        label: "High severity after",
        tooltip: "Days since last review before the signal escalates to high severity",
        unit: "days"
      }
    ]
  },
  {
    type: "NEW_MANAGER_NO_REVIEW",
    label: "New Manager — No Review",
    description: "Raised when an employee's manager has changed but no review has been held since the transition.",
    severity: "high"
  },
  {
    type: "LONG_TENURE_NO_REVIEW",
    label: "Long Tenure — Never Reviewed",
    description: "Employee has reached the tenure threshold without ever having a performance review.",
    severity: "high",
    thresholds: [
      {
        key: "longTenureDays",
        label: "Tenure threshold",
        tooltip: "Days since start date after which this signal is triggered",
        unit: "days"
      }
    ]
  },
  {
    type: "ROLE_CHANGE_REQUESTED",
    label: "Role Change Requested",
    description: "Employee has flagged that they want to change their role or project.",
    severity: "high"
  },
  {
    type: "FIRST_REVIEW_UPCOMING",
    label: "Probation Review Due",
    description: "Employee is within the probation review window and no review has been held yet.",
    severity: "medium",
    thresholds: [
      {
        key: "probationMinDays",
        label: "Window opens at",
        tooltip: "Day since start when the probation review window opens",
        unit: "days"
      },
      {
        key: "probationMaxDays",
        label: "Window closes at",
        tooltip: "Day since start when the probation review window closes",
        unit: "days"
      }
    ]
  }
];

const severityTagVariant = (
  severity: SignalConfig["severity"]
): "danger" | "warning" | "outlined" => {
  if (severity === "high") return "danger";
  if (severity === "medium") return "warning";
  return "outlined";
};

const severityLabel = (severity: SignalConfig["severity"]) => {
  if (severity === "varies") return "varies";
  return severity;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PerformanceReviewConfigRoute = () => {
  const navigate = useNavigate();
  const { config, setSignalEnabled, setThreshold, resetToDefaults } =
    usePerformanceReviewConfig();

  return (
    <TopBarRoute
      paths={[
        {
          key: "performance-reviews",
          title: "Performance Reviews",
          onClick: () => navigate("/performance-reviews")
        },
        { key: "config", title: "Configuration" }
      ]}
      medium
    >
      <Stack vertical gap="xLarge">
        <Stack justifyContent="space-between" alignItems="flex-start">
          <Stack vertical gap="xSmall">
            <Title variant="bold">Signal configuration</Title>
            <PageDescription>
              Enable or disable signals and adjust the thresholds used to flag employees for
              review. Changes are saved automatically.
            </PageDescription>
          </Stack>
          <Button size="small" variant="outlined" onClick={resetToDefaults}>
            Reset to defaults
          </Button>
        </Stack>

        <Stack vertical gap="medium">
          {signalConfigs.map(signal => {
            const enabled = config.enabledSignals[signal.type];
            return (
              <SignalCard key={signal.type} $disabled={!enabled}>
                <SignalHeader>
                  <SignalMeta>
                    <SignalTitleRow>
                      <Title variant="bold">{signal.label}</Title>
                      <Tag colorVariant={severityTagVariant(signal.severity)}>
                        {severityLabel(signal.severity)}
                      </Tag>
                    </SignalTitleRow>
                    <Muted>{signal.description}</Muted>
                  </SignalMeta>
                  <Toggle
                    checked={enabled}
                    onChange={checked => setSignalEnabled(signal.type, checked)}
                  />
                </SignalHeader>

                {signal.thresholds && signal.thresholds.length > 0 && (
                  <ThresholdSection $visible={enabled}>
                    {signal.thresholds.map(t => (
                      <ThresholdRow key={t.key}>
                        <Tooltip tooltip={t.tooltip} placement="top">
                          <ThresholdLabel>{t.label}</ThresholdLabel>
                        </Tooltip>
                        <Input
                          type="number"
                          size="small"
                          value={config.thresholds[t.key].toString()}
                          endAdornment={<Muted>{t.unit}</Muted>}
                          onChange={value => {
                            const val = parseInt(value, 10);
                            if (!isNaN(val) && val > 0) setThreshold(t.key, val);
                          }}
                        />
                      </ThresholdRow>
                    ))}
                  </ThresholdSection>
                )}
              </SignalCard>
            );
          })}
        </Stack>
      </Stack>
    </TopBarRoute>
  );
};
